// Helpers for turning the leaderboard to landscape.
//
// Two mechanisms, in preference order:
//
// 1. The native app bridge. WebKit has never implemented
//    `screen.orientation.lock()`, so no iOS surface can rotate itself — the app
//    rotates natively instead and exposes `window.ReactNativeWebView`
//    (genuinely in the React Native build, shimmed onto WKWebView by
//    LeaderboardOrientationBridge) to receive the request. It toggles from the
//    current orientation, so one message serves both directions, and it leaves
//    both orientations allowed so the user can still turn the device by hand.
// 2. The Screen Orientation API, which also needs fullscreen (Android Chrome).
//
// Where neither exists — iOS in a browser — nothing can rotate the page and
// callers must handle a rejected lock.

export const nativeRotateSupported = () =>
  typeof window !== 'undefined' &&
  !!window.ReactNativeWebView &&
  typeof window.ReactNativeWebView.postMessage === 'function'

export const orientationLockSupported = () =>
  typeof screen !== 'undefined' &&
  screen.orientation &&
  typeof screen.orientation.lock === 'function'

// Whether anything at all can change the orientation from the page.
export const orientationChangeSupported = () =>
  nativeRotateSupported() || orientationLockSupported()

// The app matches on `type`, and flips based on the orientation it is currently
// in, so the same message rotates to landscape and back.
const requestNativeRotate = () => {
  try {
    window.ReactNativeWebView.postMessage(JSON.stringify({type: 'rotate'}))
    return Promise.resolve()
  } catch (error) {
    return Promise.reject(error)
  }
}

const requestFullscreen = (element) => {
  const request = element.requestFullscreen || element.webkitRequestFullscreen
  return request ? Promise.resolve(request.call(element)) : Promise.resolve()
}

const exitFullscreen = () => {
  const exit = document.exitFullscreen || document.webkitExitFullscreen
  const isFullscreen = document.fullscreenElement || document.webkitFullscreenElement
  if (isFullscreen && exit) {
    try {
      exit.call(document)
    } catch (error) { /* no-op */
    }
  }
}

export const lockLandscape = (element) => {
  if (nativeRotateSupported()) {
    return requestNativeRotate()
  }

  if (!orientationLockSupported()) {
    return Promise.reject(new Error('orientation lock unsupported'))
  }

  return requestFullscreen(element || document.documentElement)
    .then(() => screen.orientation.lock('landscape'))
    .catch((error) => {
      // Fullscreen can succeed while the lock still fails. Leaving the page
      // fullscreen-but-unlocked strands the user: the button that exits is
      // itself hidden once the wider layout stops counting as a mobile view,
      // and a phone has no keyboard to press Escape with. Roll it back.
      exitFullscreen()
      throw error
    })
}

export const unlockOrientation = () => {
  if (nativeRotateSupported()) {
    requestNativeRotate()
    return
  }

  if (typeof screen !== 'undefined' && screen.orientation && screen.orientation.unlock) {
    try {
      screen.orientation.unlock()
    } catch (error) { /* no-op */
    }
  }
  exitFullscreen()
}
